import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiCall } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import { formatToLocalDateTime } from '../utils/date';

const WalletComponent = ({ userId, accessToken, onRefresh, showTitle = true }) => {
    const { refreshAccessToken } = useAuth();
    const [balance, setBalance] = useState(null);
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const fetchBalanceRef = useRef(null);
    const notifyWalletBalance = useCallback((payload) => {
        if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
            return;
        }

        try {
            const rawBalance = payload?.balance;
            const numericBalance = typeof rawBalance === 'number' ? rawBalance : Number(rawBalance);
            const detailBalance = Number.isFinite(numericBalance) ? numericBalance : null;

            window.dispatchEvent(new CustomEvent('walletBalanceUpdated', {
                detail: {
                    balance: detailBalance,
                    lastUpdated: payload?.last_updated ?? null,
                    source: 'WalletComponent',
                },
            }));
        } catch (eventError) {
            console.warn('[WalletComponent] Failed to dispatch walletBalanceUpdated event:', eventError);
        }
    }, []);

    const fetchBalance = useCallback(async (token = accessToken) => {
        if (!userId || !token) {
            console.log('[WalletComponent] Missing userId or accessToken, skipping fetch');
            setMessage('Please sign in to view your wallet.');
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        console.log('[WalletComponent] Fetching balance for userId:', userId);
        try {
            const balanceData = await apiCall(
                '/wallet/balance?product_id=2',
                'GET',
                null,
                true, // Use Aurum API
                null, // No x-user-id header
                token
            );
            console.log('[WalletComponent] Balance API response:', balanceData);

            // If last_updated is missing, fetch the latest transaction to infer it
            let updatedBalance = { ...balanceData };
            if (!balanceData.last_updated) {
                try {
                    const transactionData = await apiCall(
                        '/wallet/transactions?product_id=2&skip=0&limit=1',
                        'GET',
                        null,
                        true,
                        null,
                        token
                    );
                    console.log('[WalletComponent] Transaction API response:', transactionData);
                    const latestTransaction = Array.isArray(transactionData) && transactionData.length > 0 ? transactionData[0] : null;
                    if (latestTransaction && latestTransaction.created_at) {
                        updatedBalance.last_updated = latestTransaction.created_at;
                        console.log('[WalletComponent] Inferred last_updated from transaction:', updatedBalance.last_updated);
                    }
                } catch (txError) {
                    console.warn('[WalletComponent] Failed to fetch transactions for last_updated:', txError);
                }
            }
            setBalance(updatedBalance);
            notifyWalletBalance(updatedBalance);
            setMessage('');
        } catch (error) {
            console.error('[WalletComponent] Error fetching balance:', error);
            console.log('[WalletComponent] Error details:', {
                message: error.message,
                status: error.status,
                response: error.response,
            });
            if (error.message.includes('401')) {
                try {
                    console.log('[WalletComponent] Attempting token refresh');
                    const newToken = await refreshAccessToken();
                    await fetchBalance(newToken); // Retry with new token
                } catch (refreshError) {
                    console.error('[WalletComponent] Token refresh failed:', refreshError);
                    setMessage('Authentication failed. Please sign in again.');
                }
            } else if (
                error.status === 404 ||
                error.message.toLowerCase().includes('not found') ||
                error.message.includes('API Error: 404')
            ) {
                console.log('[WalletComponent] Handling 404 as zero balance');
                const zeroBalance = { balance: 0, last_updated: new Date().toISOString() };
                setBalance(zeroBalance);
                notifyWalletBalance(zeroBalance);
                setMessage('');
            } else {
                setMessage(`Error fetching balance: ${error.message}`);
                notifyWalletBalance(null);
            }
        } finally {
            setIsLoading(false);
        }
    }, [userId, accessToken, refreshAccessToken, notifyWalletBalance]);

    useEffect(() => {
        fetchBalanceRef.current = fetchBalance;
        if (onRefresh) {
            console.log('[WalletComponent] Setting onRefresh callback');
            onRefresh(fetchBalanceRef);
        }
    }, [fetchBalance, onRefresh]);

    useEffect(() => {
        console.log('[WalletComponent] Initial fetch on mount');
        fetchBalance();
    }, [fetchBalance]);

    if (!userId || !accessToken) {
        console.log('[WalletComponent] Render: No userId or accessToken, showing sign-in message');
        return <p>Please sign in to view your wallet.</p>;
    }

    console.log('[WalletComponent] Rendering with balance:', balance, 'message:', message);
    return (
        <div className="wallet-container">
            {showTitle && <h2 className="wallet-container__title">Wallet</h2>}
            {isLoading && <p>Loading balance...</p>}
            {!isLoading && balance !== null ? (
                <div className="wallet-balance">
                    <span className="wallet-balance__amount">
                        {typeof balance.balance === 'number' ? balance.balance.toLocaleString() : balance.balance}
                        <span className="wallet-balance__unit"> points</span>
                    </span>
                    <span className="wallet-balance__meta">
                        Last updated: {formatToLocalDateTime(balance.last_updated, { fallback: 'Not available', includeSeconds: false })}
                    </span>
                </div>
            ) : (
                !isLoading && <p className="wallet-balance__empty">No balance data available.</p>
            )}
            {message && <p className="error-text">{message}</p>}
        </div>
    );
};

export default WalletComponent;